import { useTranslation } from "react-i18next";
import { FormButtons, RestoreBlock } from "./LogInStyles";
import * as Yup from "yup";
import { useState } from "react";
import { Field, Form, Formik, FormikHelpers } from "formik";
import axios from "axios";
import { NavLink, useNavigate } from "react-router-dom";
import { Loader } from "../../../components/Loader/Loader";
import {
  ArticleBlock,
  FormMainBlock,
  ShowPasswordBlock,
  TheLine,
} from "../FormsGeneralStyles";
import { LoginFormValues } from "../../../interfaces/LoginInterface";
import { useAppSelector } from "../../../store/hooks";
import { generateLink, generateRedirectUrl } from "../../../functions/routingFuntion";

export const LogIn = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isSuccess, setSuccess] = useState(false);
  const [inProgress, setInProgress] = useState(false);
  const lang = useAppSelector((state) => state.locale.localStorageValue);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const DisplayingErrorMessagesSchema = Yup.object().shape({
    password: Yup.string().required(t("formsWarnings.passwordRequired")),
    email: Yup.string()
      .email(t("formsWarnings.invalidEmail"))
      .required(t("formsWarnings.emailRequired")),
  });

  const handleSubmit = async (
    values: LoginFormValues,
    formikHelpers: FormikHelpers<LoginFormValues>
  ) => {
    setInProgress(true);
    setSuccess(false);

    try {
      const response = await axios.post(
        "https://api.demo-q17.com/auth/login",
        values,
        {
          headers: {
            "Content-Type": "application/json",
            "Accept-Language": lang,
            "Accept": "application/json",
          },
        }
      );

      setSuccess(true);

      const redirectUrl = generateRedirectUrl(
        "https://terminal.demo-q17.com",
        lang
      );
      window.location.href = redirectUrl;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const { status, data } = error.response!;

        if (status === 422 && data.errors?.two_enabled) {
          localStorage.setItem("email", values.email);
          localStorage.setItem("password", values.password);
          if (lang === "en") {
            navigate("/auth/2fa");
          } else {
            navigate(`/${lang}/auth/2fa`);
          }
        } else {
          if (status === 422 && data.errors) {
            Object.keys(data.errors).forEach((field) => {
              formikHelpers.setFieldError(field, data.errors[field]);
            });
          }
        }

        if (status === 401) {
          formikHelpers.setFieldError(
            "password",
            t("formsWarnings.invalidCredentials")
          );
          formikHelpers.setFieldError(
            "email",
            t("formsWarnings.invalidCredentials")
          );
        }

        if (status === 429) {
          alert(t("formsWarnings.tooManyRequests"));
        }
      } else {
        console.error("Unexpected error:", error);
      }
    } finally {
      setInProgress(false);
    }
  };

  return (
    <main>
      <FormMainBlock>
        <section>
          <div className="container__standart div__container">
            <div className="div__form">
              <h3>{t("logInPage.title1")}</h3>
              <Formik
                initialValues={{
                  email: "",
                  password: "",
                  remember: true,
                }}
                validationSchema={DisplayingErrorMessagesSchema}
                onSubmit={handleSubmit}
              >
                {({ errors, touched }) => (
                  <Form className="form__content">
                    <ArticleBlock>
                      <div className="div__main__block">
                        <Field
                          id="email"
                          name="email"
                          placeholder={t("form.mail")}
                          type="text"
                          className={`input__styles ${
                            touched.email && errors.email && "!border-[#eb5757]"
                          }`}
                        />
                        {touched.email && errors.email && (
                          <span className="form__req__info">
                            {errors.email}
                          </span>
                        )}
                      </div>
                    </ArticleBlock>
                    <ArticleBlock>
                      <div className="div__main__block">
                        <Field
                          id="password"
                          name="password"
                          placeholder={t("form.password")}
                          type={showPassword ? "text" : "password"}
                          className={`input__styles ${
                            touched.password &&
                            errors.password &&
                            "!border-[#eb5757]"
                          }`}
                        />
                        <ShowPasswordBlock onClick={togglePasswordVisibility}>
                          <span></span>
                        </ShowPasswordBlock>
                        {touched.password && errors.password && (
                          <span className="form__req__info">
                            {errors.password}
                          </span>
                        )}
                      </div>
                    </ArticleBlock>
                    <RestoreBlock>
                      <span className="forgot__block">
                        {t("logInPage.blockInfo11")}
                      </span>
                      <NavLink
                        to={generateLink("/auth/forgot-password", lang)}
                        className="restore__link"
                      >
                        {t("logInPage.blockInfo12")}
                      </NavLink>
                    </RestoreBlock>
                    <TheLine>
                      <span></span>
                    </TheLine>
                    <FormButtons>
                      <NavLink
                        to={generateLink("/auth/register", lang)}
                        className="white__button flex__center reg__btn"
                      >
                        {t("form.register")}
                      </NavLink>
                      {inProgress ? (
                        <Loader height="41" width="100%" />
                      ) : (
                        <button
                          type="submit"
                          className="white__button flex__center log__btn"
                        >
                          {isSuccess
                            ? t("form.successfully")
                            : t("form.signIn")}
                        </button>
                      )}
                    </FormButtons>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </section>
      </FormMainBlock>
    </main>
  );
};
